import { useState, useEffect } from "react";
import { useAdminService } from "../../services/adminService";
import { useToastHook } from "../../utils/useToast";
import { urlimage } from "../../utils/config";

const ACTIONS = {
  classes: { label: "Générer les classes", fn: "genererClassesStandard" },
  matieres: { label: "Générer les matières", fn: "genererMatieresStandard" },
};

// Étapes gérables entièrement dans le panneau du checklist
const GERABLES = ["etablissement", "annee_scolaire", "classes", "matieres", "carte", "bulletin"];

const CYCLE_LABELS = {
  PRESCOLAIRE: "Préscolaire",
  PRIMAIRE: "Primaire",
  COLLEGE: "Collège",
  LYCEE: "Lycée",
};

const TYPE_ETABLISSEMENT_OPTIONS = [
  { value: "PUBLIC", label: "Public" },
  { value: "PRIVE", label: "Privé" },
  { value: "CONFESSIONNEL", label: "Confessionnel" },
];

const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  if (path.startsWith("/")) return `${urlimage}${path}`;
  return `${urlimage}/${path}`;
};

const EMPTY_ETAB_FORM = {
  nom: "",
  sigle: "",
  numero_ecole: "",
  email: "",
  telephone: "",
  adresse: "",
  site_web: "",
  pays: "BURKINA FASO",
  devise_pays: "La patrie ou la mort, nous vaincrons",
  slogan: "",
  devise_ecole: "",
};

const dictToRows = (dict) =>
  dict && typeof dict === "object"
    ? Object.entries(dict).map(([seuil, texte]) => ({ seuil, texte }))
    : [];
const rowsToDict = (rows) =>
  rows.reduce((acc, r) => {
    if (r.seuil !== "" && r.texte.trim() !== "") acc[r.seuil] = r.texte.trim();
    return acc;
  }, {});

export default function ADSetupChecklist({ slug, onNavigateTab }) {
  const [status, setStatus] = useState(null);
  const [running, setRunning] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [closing, setClosing] = useState(false);
  const [hidden, setHidden] = useState(
    () => sessionStorage.getItem(`setup-hidden-${slug}`) === "1"
  );

  const [expanded, setExpanded] = useState(null); // 'preview-classes' | cle d'étape gérée | null
  const [niveaux, setNiveaux] = useState(null);
  const [cyclesEtab, setCyclesEtab] = useState(null);
  const [items, setItems] = useState({});         // { classes: [...], matieres: [...], annee_scolaire: [...], carte: [...], bulletin: [...] }
  const [loadingPanel, setLoadingPanel] = useState(false);

  // Édition classes / matières
  const [editingId, setEditingId] = useState(null);
  const [editNom, setEditNom] = useState("");
  const [editCoefficient, setEditCoefficient] = useState("");
  const [savingId, setSavingId] = useState(null);

  // Suppression classes / matières
  const [deletingId, setDeletingId] = useState(null);
  const [deleteBusyId, setDeleteBusyId] = useState(null);

  // Ajout classes / matières
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newNiveauId, setNewNiveauId] = useState("");
  const [newCoefficient, setNewCoefficient] = useState("1");
  const [adding, setAdding] = useState(false);

  // Année scolaire — création
  const [aeAddOpen, setAeAddOpen] = useState(false);
  const [aeNom, setAeNom] = useState("");
  const [aeDateDebut, setAeDateDebut] = useState("");
  const [aeDateFin, setAeDateFin] = useState("");
  const [aeSaving, setAeSaving] = useState(false);
  const [aeActivatingId, setAeActivatingId] = useState(null);

  // Année scolaire — édition
  const [aeEditingId, setAeEditingId] = useState(null);
  const [aeEditNom, setAeEditNom] = useState("");
  const [aeEditDateDebut, setAeEditDateDebut] = useState("");
  const [aeEditDateFin, setAeEditDateFin] = useState("");
  const [aeEditSaving, setAeEditSaving] = useState(false);

  // Année scolaire — suppression (avec vérification "année non vide")
  const [aeDeletingId, setAeDeletingId] = useState(null);
  const [aeDeleteBusyId, setAeDeleteBusyId] = useState(null);
  const [aeDeleteChecking, setAeDeleteChecking] = useState(false);
  const [aeDeleteStats, setAeDeleteStats] = useState(null);

  // Année scolaire — archivage / restauration
  const [aeArchivingId, setAeArchivingId] = useState(null);
  const [aeRestoringId, setAeRestoringId] = useState(null);

  // Établissement
  const [etabEditOpen, setEtabEditOpen] = useState(false);
  const [etabForm, setEtabForm] = useState(EMPTY_ETAB_FORM);
  const [etabSaving, setEtabSaving] = useState(false);
  const [etabDetail, setEtabDetail] = useState(null);
  const [etabLogoFile, setEtabLogoFile] = useState(null);
  const [etabCachetFile, setEtabCachetFile] = useState(null);
  const [etabLogoPaysFile, setEtabLogoPaysFile] = useState(null);
  const [etabLogoPreview, setEtabLogoPreview] = useState(null);
  const [etabCachetPreview, setEtabCachetPreview] = useState(null);
  const [etabLogoPaysPreview, setEtabLogoPaysPreview] = useState(null);

  // Configuration carte
  const [carteAddOpen, setCarteAddOpen] = useState(false);
  const [carteEditingId, setCarteEditingId] = useState(null);
  const [carteForm, setCarteForm] = useState({ annee_scolaire: "", coordonnees_carte: "", actif: false });
  const [carteSaving, setCarteSaving] = useState(false);
  const [carteDeletingId, setCarteDeletingId] = useState(null);
  const [carteDeleteBusyId, setCarteDeleteBusyId] = useState(null);
  const [carteActivatingId, setCarteActivatingId] = useState(null);

  // Configuration bulletin
  const [bulletinAddOpen, setBulletinAddOpen] = useState(false);
  const [bulletinEditingId, setBulletinEditingId] = useState(null);
  const [bulletinForm, setBulletinForm] = useState({ cycle: "", annee_scolaire: "", note_maximale: "20.00", est_actif: true });
  const [bulletinRowsEducateur, setBulletinRowsEducateur] = useState([]);
  const [bulletinRowsEcole, setBulletinRowsEcole] = useState([]);
  const [bulletinSaving, setBulletinSaving] = useState(false);
  const [bulletinDeletingId, setBulletinDeletingId] = useState(null);
  const [bulletinDeleteBusyId, setBulletinDeleteBusyId] = useState(null);
  const [bulletinActivatingId, setBulletinActivatingId] = useState(null);

  const toast = useToastHook();
  const {
    getSetupStatus,
    genererClassesStandard,
    genererMatieresStandard,
    getNiveaux,
    getClasses, createClasse, updateClasse, deleteClasse,
    getMatieres, createMatiere, updateMatiere, deleteMatiere,
    getAnneesScolaires, createAnneeScolaire, updateAnneeScolaire, setActiveAnneeScolaire,
    archiveAnneeScolaire, restoreAnneeScolaire, deleteAnneeScolaire, getAnneeScolaireStats,
    getEtablissementCourant, updateEtablissement,
    getConfigurationsCarte, createConfigurationCarte, updateConfigurationCarte,
    updateConfigurationCartePartial, deleteConfigurationCarte,
    getBulletinConfigurations, createBulletinConfiguration, updateBulletinConfiguration,
    updateBulletinConfigurationPartial, deleteBulletinConfiguration,
  } = useAdminService();

  const load = async () => {
    try {
      const data = await getSetupStatus(slug);
      setStatus(data);
    } catch (error) {
    }
  };

  useEffect(() => { if (slug) load(); }, [slug]);

  const runners = { genererClassesStandard, genererMatieresStandard };

  const closeAllPanels = () => {
    setExpanded(null);
    setEditingId(null);
    setDeletingId(null);
    setAddOpen(false);
    setNewName(""); setNewNiveauId(""); setNewCoefficient("1");
    setAeAddOpen(false);
    setAeNom(""); setAeDateDebut(""); setAeDateFin("");
    setAeEditingId(null); setAeEditNom(""); setAeEditDateDebut(""); setAeEditDateFin("");
    setAeDeletingId(null); setAeDeleteStats(null); setAeDeleteChecking(false);
    setEtabEditOpen(false);
    setEtabLogoFile(null); setEtabCachetFile(null); setEtabLogoPaysFile(null);
    setCarteAddOpen(false); setCarteEditingId(null); setCarteDeletingId(null);
    setBulletinAddOpen(false); setBulletinEditingId(null); setBulletinDeletingId(null);
    setBulletinRowsEducateur([]); setBulletinRowsEcole([]);
  };

  const handleAction = async (cle) => {
    const action = ACTIONS[cle];
    if (!action || !status?.annee_scolaire_active_id) return;
    setRunning(cle);
    try {
      await runners[action.fn](slug, status.annee_scolaire_active_id);
      toast.success(`${action.label} : terminé`);
      await load();
      setItems(prev => ({ ...prev, [cle]: undefined }));
    } catch (error) {
      toast.error(error.response?.data?.error || "Une erreur est survenue");
    } finally {
      setRunning(null);
    }
  };

  const handleMasquer = () => {
    setClosing(true);
    setTimeout(() => {
      sessionStorage.setItem(`setup-hidden-${slug}`, "1");
      setHidden(true);
    }, 280);
  };

  const ensureNiveaux = async () => {
    if (niveaux) return niveaux;
    try {
      const list = await getNiveaux(slug);
      const arr = Array.isArray(list) ? list : list?.results || [];
      setNiveaux(arr);
      return arr;
    } catch {
      toast.error("Impossible de charger les niveaux");
      return [];
    }
  };

  const ensureEtabDetail = async () => {
    if (etabDetail) return etabDetail;
    try {
      const detail = await getEtablissementCourant(slug);
      setEtabDetail(detail);
      return detail;
    } catch {
      toast.error("Impossible de charger les informations de l'établissement");
      return null;
    }
  };

  const ensureAnneesList = async () => {
    if (items.annee_scolaire) return items.annee_scolaire;
    try {
      const list = await getAnneesScolaires(slug);
      const arr = Array.isArray(list) ? list : list?.results || [];
      setItems(prev => ({ ...prev, annee_scolaire: arr }));
      return arr;
    } catch {
      toast.error("Impossible de charger les années scolaires");
      return [];
    }
  };

  const ensureCyclesEtab = async () => {
    if (cyclesEtab) return cyclesEtab;
    const detail = await ensureEtabDetail();
    const cycles = detail?.cycles || [];
    setCyclesEtab(cycles);
    return cycles;
  };

  const refreshAnneesList = async () => {
    try {
      const list = await getAnneesScolaires(slug, { show_archived: "true" });
      const arr = Array.isArray(list) ? list : list?.results || [];
      setItems(prev => ({ ...prev, annee_scolaire: arr }));
      return arr;
    } catch {
      toast.error("Impossible de charger les années scolaires");
      return [];
    }
  };

  const refreshCarteList = async () => {
    try {
      const list = await getConfigurationsCarte(slug);
      setItems(prev => ({ ...prev, carte: Array.isArray(list) ? list : list?.results || [] }));
    } catch {
      toast.error("Impossible de charger les configurations cartes");
    }
  };

  const refreshBulletinList = async () => {
    try {
      const list = await getBulletinConfigurations(slug);
      setItems(prev => ({ ...prev, bulletin: Array.isArray(list) ? list : list?.results || [] }));
    } catch {
      toast.error("Impossible de charger les configurations bulletins");
    }
  };

  const togglePreviewClasses = async () => {
    if (expanded === "preview-classes") { closeAllPanels(); return; }
    closeAllPanels();
    setExpanded("preview-classes");
    setLoadingPanel(true);
    await ensureNiveaux();
    setLoadingPanel(false);
  };

  // ── Ouverture d'un panneau de gestion, quelle que soit l'étape ──
  const toggleManage = async (cle) => {
    if (expanded === cle) { closeAllPanels(); return; }
    closeAllPanels();
    setExpanded(cle);
    setLoadingPanel(true);
    try {
      if (cle === "classes") {
        await ensureNiveaux();
        if (!items.classes) {
          const list = await getClasses(slug, { annee_scolaire: status.annee_scolaire_active_id });
          setItems(prev => ({ ...prev, classes: Array.isArray(list) ? list : list?.results || [] }));
        }
      } else if (cle === "matieres") {
        if (!items.matieres) {
          const list = await getMatieres(slug, { annee_scolaire: status.annee_scolaire_active_id });
          setItems(prev => ({ ...prev, matieres: Array.isArray(list) ? list : list?.results || [] }));
        }
      } else if (cle === "annee_scolaire") {
        await refreshAnneesList();
      } else if (cle === "etablissement") {
        const detail = etabDetail || await getEtablissementCourant(slug);
        setEtabDetail(detail);
        setEtabForm({
          nom: detail.nom || "",
          sigle: detail.sigle || "",
          numero_ecole: detail.numero_ecole || "",
          email: detail.email || "",
          telephone: detail.telephone || "",
          adresse: detail.adresse || "",
          site_web: detail.site_web || "",
          pays: detail.pays || "BURKINA FASO",
          devise_pays: detail.devise_pays || "La patrie ou la mort, nous vaincrons",
          slogan: detail.slogan || "",
          devise_ecole: detail.devise_ecole || "",
        });
        setEtabLogoFile(null); setEtabCachetFile(null); setEtabLogoPaysFile(null);
        setEtabLogoPreview(detail.logo ? getImageUrl(detail.logo) : null);
        setEtabCachetPreview(detail.cachet ? getImageUrl(detail.cachet) : null);
        setEtabLogoPaysPreview(detail.logo_pays ? getImageUrl(detail.logo_pays) : null);
      } else if (cle === "carte") {
        await ensureAnneesList();
        await refreshCarteList();
      } else if (cle === "bulletin") {
        await Promise.all([ensureAnneesList(), ensureCyclesEtab()]);
        await refreshBulletinList();
      }
    } catch {
      toast.error("Impossible de charger les données");
    } finally {
      setLoadingPanel(false);
    }
  };

  // ── Classes / Matières : édition ──
  const startEdit = (item) => {
    setAddOpen(false);
    setDeletingId(null);
    setEditingId(item.id);
    setEditNom(item.nom);
    setEditCoefficient(item.coefficient != null ? String(item.coefficient) : "1");
  };
  const cancelEdit = () => { setEditingId(null); setEditNom(""); setEditCoefficient(""); };

  const saveEdit = async (cle, id) => {
    const nom = editNom.trim();
    if (!nom) { toast.error("Le nom ne peut pas être vide"); return; }
    const payload = { nom };
    if (cle === "matieres") {
      const coef = parseInt(editCoefficient, 10);
      if (!coef || coef < 1) { toast.error("Le coefficient doit être un nombre positif"); return; }
      payload.coefficient = coef;
    }
    setSavingId(id);
    try {
      const updater = cle === "classes" ? updateClasse : updateMatiere;
      await updater(slug, id, payload);
      setItems(prev => ({ ...prev, [cle]: prev[cle].map(it => (it.id === id ? { ...it, ...payload } : it)) }));
      toast.success("Modifié");
      cancelEdit();
    } catch (error) {
      toast.error(error.response?.data?.error || "Erreur lors de la modification");
    } finally {
      setSavingId(null);
    }
  };

  // ── Classes / Matières : suppression ──
  const askDelete = (item) => { setEditingId(null); setAddOpen(false); setDeletingId(item.id); };
  const cancelDelete = () => setDeletingId(null);

  const confirmDelete = async (cle, id) => {
    setDeleteBusyId(id);
    try {
      const deleter = cle === "classes" ? deleteClasse : deleteMatiere;
      await deleter(slug, id);
      setItems(prev => ({ ...prev, [cle]: prev[cle].filter(it => it.id !== id) }));
      toast.success(cle === "classes" ? "Classe supprimée" : "Matière supprimée");
      setDeletingId(null);
    } catch (error) {
      toast.error(error.response?.data?.error || "Suppression impossible");
    } finally {
      setDeleteBusyId(null);
    }
  };

  // ── Classes / Matières : ajout ──
  const openAdd = () => {
    cancelEdit(); setDeletingId(null);
    setAddOpen(true); setNewName(""); setNewNiveauId(""); setNewCoefficient("1");
  };
  const cancelAdd = () => { setAddOpen(false); setNewName(""); setNewNiveauId(""); setNewCoefficient("1"); };

  const submitAdd = async (cle) => {
    const nom = newName.trim();
    if (!nom) { toast.error("Le nom est requis"); return; }
    if (cle === "classes" && !newNiveauId) { toast.error("Sélectionnez un niveau"); return; }

    let coefficient = 1;
    if (cle === "matieres") {
      coefficient = parseInt(newCoefficient, 10);
      if (!coefficient || coefficient < 1) { toast.error("Le coefficient doit être un nombre positif"); return; }
    }

    setAdding(true);
    try {
      const created = cle === "classes"
        ? await createClasse(slug, {
            nom, niveau: newNiveauId,
            etablissement: status.etablissement_id,
            annee_scolaire: status.annee_scolaire_active_id,
          })
        : await createMatiere(slug, {
            nom, annee_scolaire: status.annee_scolaire_active_id, coefficient,
          });
      setItems(prev => ({ ...prev, [cle]: [...(prev[cle] || []), created] }));
      toast.success("Ajouté");
      cancelAdd();
    } catch (error) {
      toast.error(error.response?.data?.error || "Erreur lors de l'ajout");
    } finally {
      setAdding(false);
    }
  };

  // ── Année scolaire : création ──
  const openAeAdd = () => {
    setAeEditingId(null);
    setAeAddOpen(true);
    setAeNom(""); setAeDateDebut(""); setAeDateFin("");
  };
  const cancelAeAdd = () => setAeAddOpen(false);

  const submitAeAdd = async () => {
    const nom = aeNom.trim();
    if (!nom) { toast.error("Le nom est requis (ex: 2026-2027)"); return; }
    setAeSaving(true);
    try {
      const payload = { nom, etablissement: status.etablissement_id };
      if (aeDateDebut) payload.date_debut = aeDateDebut;
      if (aeDateFin) payload.date_fin = aeDateFin;
      const created = await createAnneeScolaire(slug, payload);
      setItems(prev => ({ ...prev, annee_scolaire: [...(prev.annee_scolaire || []), created] }));
      toast.success("Année scolaire créée");
      cancelAeAdd();
      await load();
    } catch (error) {
      toast.error(error.response?.data?.nom?.[0] || error.response?.data?.error || "Erreur lors de la création");
    } finally {
      setAeSaving(false);
    }
  };

  const handleActivateAnnee = async (id) => {
    setAeActivatingId(id);
    try {
      await setActiveAnneeScolaire(slug, id);
      setItems(prev => ({
        ...prev,
        annee_scolaire: prev.annee_scolaire.map(a => ({ ...a, actif: a.id === id })),
      }));
      toast.success("Année scolaire activée");
      await load();
    } catch (error) {
      toast.error(error.response?.data?.error || "Impossible d'activer cette année");
    } finally {
      setAeActivatingId(null);
    }
  };

  // ── Année scolaire : édition ──
  const startAeEdit = (item) => {
    if (item.archived) { toast.info("Cette année est archivée et ne peut pas être modifiée"); return; }
    setAeAddOpen(false); setAeDeletingId(null); setAeDeleteStats(null);
    setAeEditingId(item.id);
    setAeEditNom(item.nom);
    setAeEditDateDebut(item.date_debut || "");
    setAeEditDateFin(item.date_fin || "");
  };
  const cancelAeEdit = () => {
    setAeEditingId(null); setAeEditNom(""); setAeEditDateDebut(""); setAeEditDateFin("");
  };

  const saveAeEdit = async (id) => {
    const nom = aeEditNom.trim();
    if (!nom) { toast.error("Le nom est requis"); return; }
    setAeEditSaving(true);
    try {
      const payload = {
        nom,
        date_debut: aeEditDateDebut || null,
        date_fin: aeEditDateFin || null,
      };
      const updated = await updateAnneeScolaire(slug, id, payload);
      setItems(prev => ({
        ...prev,
        annee_scolaire: prev.annee_scolaire.map(a => (a.id === id ? updated : a)),
      }));
      toast.success("Année scolaire modifiée");
      cancelAeEdit();
    } catch (error) {
      toast.error(error.response?.data?.nom?.[0] || error.response?.data?.date_fin || error.response?.data?.error || "Erreur lors de la modification");
    } finally {
      setAeEditSaving(false);
    }
  };

  // ── Année scolaire : archivage / restauration ──
  const handleAeArchive = async (id) => {
    setAeArchivingId(id);
    try {
      const res = await archiveAnneeScolaire(slug, id);
      setItems(prev => ({
        ...prev,
        annee_scolaire: prev.annee_scolaire.map(a => (a.id === id ? res.annee : a)),
      }));
      toast.success(res.message || "Année scolaire archivée");
      setAeDeletingId(null); setAeDeleteStats(null);
    } catch (error) {
      toast.error(error.response?.data?.error || "Impossible d'archiver cette année");
    } finally {
      setAeArchivingId(null);
    }
  };

  const handleAeRestore = async (id) => {
    setAeRestoringId(id);
    try {
      const res = await restoreAnneeScolaire(slug, id);
      setItems(prev => ({
        ...prev,
        annee_scolaire: prev.annee_scolaire.map(a => (a.id === id ? res.annee : a)),
      }));
      toast.success(res.message || "Année scolaire restaurée");
    } catch (error) {
      toast.error(error.response?.data?.error || "Impossible de restaurer cette année");
    } finally {
      setAeRestoringId(null);
    }
  };

  // ── Année scolaire : suppression (avec vérification "année non vide") ──
  const askAeDelete = async (item) => {
    if (item.actif) { toast.error("Impossible de supprimer une année active"); return; }
    setAeEditingId(null); setAeAddOpen(false);
    setAeDeletingId(item.id);
    setAeDeleteStats(null);
    setAeDeleteChecking(true);
    try {
      const stats = await getAnneeScolaireStats(slug, item.id);
      setAeDeleteStats(stats);
    } catch {
      toast.error("Impossible de vérifier les données de l'année");
      setAeDeletingId(null);
    } finally {
      setAeDeleteChecking(false);
    }
  };
  const cancelAeDelete = () => { setAeDeletingId(null); setAeDeleteStats(null); };

  const confirmAeDelete = async (id) => {
    setAeDeleteBusyId(id);
    try {
      await deleteAnneeScolaire(slug, id);
      setItems(prev => ({ ...prev, annee_scolaire: prev.annee_scolaire.filter(a => a.id !== id) }));
      toast.success("Année scolaire supprimée");
      setAeDeletingId(null); setAeDeleteStats(null);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.error || "Suppression impossible");
    } finally {
      setAeDeleteBusyId(null);
    }
  };

  // ── Établissement ──
  const handleEtabFileChange = (field, file) => {
    if (field === "logo") setEtabLogoFile(file);
    if (field === "cachet") setEtabCachetFile(file);
    if (field === "logo_pays") setEtabLogoPaysFile(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (field === "logo") setEtabLogoPreview(reader.result);
        if (field === "cachet") setEtabCachetPreview(reader.result);
        if (field === "logo_pays") setEtabLogoPaysPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const saveEtablissement = async () => {
    if (!etabForm.nom.trim()) { toast.error("Le nom est requis"); return; }
    if (!etabForm.sigle.trim()) { toast.error("Le sigle est requis"); return; }
    setEtabSaving(true);
    try {
      const formData = new FormData();
      formData.append("nom", etabForm.nom.trim());
      formData.append("sigle", etabForm.sigle.trim().toUpperCase());
      formData.append("numero_ecole", etabForm.numero_ecole || "");
      formData.append("email", etabForm.email || "");
      formData.append("telephone", etabForm.telephone || "");
      formData.append("adresse", etabForm.adresse || "");
      formData.append("site_web", etabForm.site_web || "");
      formData.append("pays", etabForm.pays || "BURKINA FASO");
      formData.append("devise_pays", etabForm.devise_pays || "La patrie ou la mort, nous vaincrons");
      formData.append("slogan", etabForm.slogan || "");
      formData.append("devise_ecole", etabForm.devise_ecole || "");
      if (etabLogoFile instanceof File) formData.append("logo", etabLogoFile);
      if (etabCachetFile instanceof File) formData.append("cachet", etabCachetFile);
      if (etabLogoPaysFile instanceof File) formData.append("logo_pays", etabLogoPaysFile);

      const updated = await updateEtablissement(slug, formData);
      setEtabDetail(updated);
      setEtabLogoFile(null); setEtabCachetFile(null); setEtabLogoPaysFile(null);
      toast.success("Informations mises à jour");
      setEtabEditOpen(false);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.error || "Erreur lors de la mise à jour");
    } finally {
      setEtabSaving(false);
    }
  };

  // ── Configuration carte ──
  const openCarteAdd = () => {
    cancelCarteEdit(); setCarteDeletingId(null);
    const anneesList = items.annee_scolaire || [];
    const anneeActive = anneesList.find(a => a.actif);
    setCarteForm({
      annee_scolaire: String((anneeActive || anneesList[0])?.id || ""),
      coordonnees_carte: "",
      actif: false,
    });
    setCarteAddOpen(true);
  };
  const cancelCarteAdd = () => setCarteAddOpen(false);

  const startCarteEdit = (item) => {
    setCarteAddOpen(false); setCarteDeletingId(null);
    setCarteEditingId(item.id);
    setCarteForm({
      annee_scolaire: item.annee_scolaire || "",
      coordonnees_carte: item.coordonnees_carte || "",
      actif: item.actif,
    });
  };
  const cancelCarteEdit = () => setCarteEditingId(null);

  const submitCarteForm = async () => {
    if (!carteForm.annee_scolaire) { toast.error("L'année scolaire est requise"); return; }
    setCarteSaving(true);
    try {
      const payload = {
        annee_scolaire: carteForm.annee_scolaire,
        actif: carteForm.actif,
        coordonnees_carte: carteForm.coordonnees_carte?.trim() || "",
      };
      if (carteEditingId) {
        await updateConfigurationCarte(slug, carteEditingId, payload);
        toast.success("Configuration modifiée");
        cancelCarteEdit();
      } else {
        await createConfigurationCarte(slug, payload);
        toast.success("Configuration créée");
        cancelCarteAdd();
      }
      await refreshCarteList();
      await load();
    } catch (error) {
      toast.error(error.response?.data?.error || "Erreur lors de la sauvegarde");
    } finally {
      setCarteSaving(false);
    }
  };

  const askDeleteCarte = (item) => { setCarteEditingId(null); setCarteAddOpen(false); setCarteDeletingId(item.id); };
  const cancelDeleteCarte = () => setCarteDeletingId(null);

  const confirmDeleteCarte = async (id) => {
    setCarteDeleteBusyId(id);
    try {
      await deleteConfigurationCarte(slug, id);
      toast.success("Configuration supprimée");
      setCarteDeletingId(null);
      await refreshCarteList();
      await load();
    } catch (error) {
      toast.error(error.response?.data?.error || "Suppression impossible");
    } finally {
      setCarteDeleteBusyId(null);
    }
  };

  const activateCarte = async (id) => {
    setCarteActivatingId(id);
    try {
      await updateConfigurationCartePartial(slug, id, { actif: true });
      toast.success("Configuration activée");
      await refreshCarteList();
      await load();
    } catch (error) {
      toast.error(error.response?.data?.error || "Impossible d'activer cette configuration");
    } finally {
      setCarteActivatingId(null);
    }
  };

  // ── Configuration bulletin ──
  const openBulletinAdd = () => {
    cancelBulletinEdit(); setBulletinDeletingId(null);
    const anneesList = items.annee_scolaire || [];
    const anneeActive = anneesList.find(a => a.actif);
    setBulletinForm({
      cycle: (cyclesEtab || []).length === 1 ? cyclesEtab[0] : "",
      annee_scolaire: String((anneeActive || anneesList[0])?.id || ""),
      note_maximale: "20.00",
      est_actif: true,
    });
    setBulletinRowsEducateur([]);
    setBulletinRowsEcole([]);
    setBulletinAddOpen(true);
  };
  const cancelBulletinAdd = () => setBulletinAddOpen(false);

  const startBulletinEdit = (item) => {
    setBulletinAddOpen(false); setBulletinDeletingId(null);
    setBulletinEditingId(item.id);
    setBulletinForm({
      cycle: item.cycle || "",
      annee_scolaire: item.annee_scolaire || "",
      note_maximale: item.note_maximale ?? "20.00",
      est_actif: item.est_actif,
    });
    setBulletinRowsEducateur(dictToRows(item.appreciation_educateur));
    setBulletinRowsEcole(dictToRows(item.appreciation_ecole));
  };
  const cancelBulletinEdit = () => { setBulletinEditingId(null); setBulletinRowsEducateur([]); setBulletinRowsEcole([]); };

  const submitBulletinForm = async () => {
    if (!bulletinForm.cycle) { toast.error("Le cycle est requis"); return; }
    if (!bulletinForm.annee_scolaire) { toast.error("L'année scolaire est requise"); return; }
    setBulletinSaving(true);
    try {
      const payload = {
        cycle: bulletinForm.cycle,
        annee_scolaire: bulletinForm.annee_scolaire,
        note_maximale: bulletinForm.note_maximale || "20.00",
        est_actif: bulletinForm.est_actif,
        appreciation_educateur: rowsToDict(bulletinRowsEducateur),
        appreciation_ecole: rowsToDict(bulletinRowsEcole),
      };
      if (bulletinEditingId) {
        await updateBulletinConfiguration(slug, bulletinEditingId, payload);
        toast.success("Configuration modifiée");
        cancelBulletinEdit();
      } else {
        await createBulletinConfiguration(slug, payload);
        toast.success("Configuration créée");
        cancelBulletinAdd();
      }
      await refreshBulletinList();
      await load();
    } catch (error) {
      toast.error(error.response?.data?.error || "Erreur lors de la sauvegarde");
    } finally {
      setBulletinSaving(false);
    }
  };

  const askDeleteBulletin = (item) => { setBulletinEditingId(null); setBulletinAddOpen(false); setBulletinDeletingId(item.id); };
  const cancelDeleteBulletin = () => setBulletinDeletingId(null);

  const confirmDeleteBulletin = async (id) => {
    setBulletinDeleteBusyId(id);
    try {
      await deleteBulletinConfiguration(slug, id);
      toast.success("Configuration supprimée");
      setBulletinDeletingId(null);
      await refreshBulletinList();
      await load();
    } catch (error) {
      toast.error(error.response?.data?.error || "Suppression impossible");
    } finally {
      setBulletinDeleteBusyId(null);
    }
  };

  const activateBulletin = async (id) => {
    setBulletinActivatingId(id);
    try {
      await updateBulletinConfigurationPartial(slug, id, { est_actif: true });
      toast.success("Configuration activée");
      await refreshBulletinList();
      await load();
    } catch (error) {
      toast.error(error.response?.data?.error || "Impossible d'activer cette configuration");
    } finally {
      setBulletinActivatingId(null);
    }
  };

  if (!status || status.progression.termine || hidden) return null;

  const { completes, total, pourcentage } = status.progression;
  const restantes = total - completes;
  const etapesActives = status.etapes.filter(e => !e.fait && e.action_requise);

  return (
    <div className={`stp-wrap ${closing ? "stp-closing" : ""}`}>
      <div className="stp-card">
        <div className="stp-glow" aria-hidden="true" />

        <button type="button" className="stp-header" onClick={() => setCollapsed(c => !c)} aria-expanded={!collapsed}>
          <div className="stp-header-left">
            <div className="stp-icon">
              <i className="ti ti-settings-2" />
              <span className="stp-icon-ping" />
            </div>
            <div className="stp-header-text">
              <div className="stp-title-row">
                <span className="stp-title">Configuration requise</span>
                <span className="stp-badge">
                  {restantes} étape{restantes > 1 ? "s" : ""} restante{restantes > 1 ? "s" : ""}
                </span>
              </div>
              <span className="stp-subtitle">
                Terminez la mise en place pour débloquer toutes les fonctionnalités de votre établissement
              </span>
            </div>
          </div>
          <div className="stp-header-right">
            <div className="stp-progress-ring">
              <svg viewBox="0 0 44 44" className="stp-ring-svg">
                <circle className="stp-ring-track" cx="22" cy="22" r="19" />
                <circle
                  className="stp-ring-fill" cx="22" cy="22" r="19"
                  style={{
                    strokeDasharray: 2 * Math.PI * 19,
                    strokeDashoffset: 2 * Math.PI * 19 * (1 - pourcentage / 100),
                  }}
                />
              </svg>
              <span className="stp-ring-label">{pourcentage}%</span>
            </div>
            <i className={`ti ti-chevron-down stp-chevron ${collapsed ? "stp-chevron-collapsed" : ""}`} />
          </div>
        </button>

        <div className="stp-bar-track">
          <div className="stp-bar-fill" style={{ width: `${pourcentage}%` }}>
            <span className="stp-bar-shimmer" />
          </div>
        </div>

        <div className={`stp-body ${collapsed ? "stp-body-collapsed" : ""}`}>
          <div className="stp-steps">
            {status.etapes.map((e, i) => {
              const isPreviewOpen = expanded === "preview-classes" && e.cle === "classes";
              const isManageOpen = expanded === e.cle && GERABLES.includes(e.cle);

              return (
                <div key={e.cle} className="stp-step-block">
                  <div className={`stp-step ${e.fait ? "stp-step-done" : "stp-step-pending"}`} style={{ '--stp-i': i }}>
                    <span className="stp-check">
                      {e.fait ? <i className="ti ti-check" /> : <span className="stp-check-dot" />}
                    </span>
                    <span className="stp-step-titre">{e.titre}</span>

                    {!e.fait && e.cle === "classes" && (
                      <button type="button" className="stp-cta stp-cta-ghost" onClick={togglePreviewClasses}>
                        Aperçu
                        <i className={`ti ti-chevron-${isPreviewOpen ? "up" : "down"}`} />
                      </button>
                    )}

                    {!e.fait && e.action_requise && ACTIONS[e.cle] && (
                      <button type="button" className="stp-cta" onClick={() => handleAction(e.cle)} disabled={running === e.cle}>
                        {running === e.cle ? <span className="stp-spinner" /> : <><i className="ti ti-bolt" />{ACTIONS[e.cle].label}</>}
                      </button>
                    )}

                    {/* Étapes toujours gérables inline, faites ou non */}
                    {(e.cle === "etablissement" || e.cle === "annee_scolaire" || e.cle === "carte" || e.cle === "bulletin") && (
                      <button type="button" className="stp-cta stp-cta-ghost" onClick={() => toggleManage(e.cle)}>
                        Gérer
                        <i className={`ti ti-chevron-${isManageOpen ? "up" : "down"}`} />
                      </button>
                    )}

                    {/* Classes / Matières : "Gérer" seulement une fois faites (avant, on génère) */}
                    {e.fait && (e.cle === "classes" || e.cle === "matieres") && (
                      <button type="button" className="stp-cta stp-cta-ghost" onClick={() => toggleManage(e.cle)}>
                        Gérer
                        <i className={`ti ti-chevron-${isManageOpen ? "up" : "down"}`} />
                      </button>
                    )}

                    {e.fait && !GERABLES.includes(e.cle) && <span className="stp-done-tag">Fait</span>}
                  </div>

                  {isPreviewOpen && (
                    <div className="stp-panel">
                      <div className="stp-panel-head">
                        <span className="stp-panel-title">Classes qui seront créées</span>
                        <span className="stp-panel-count">
                          {loadingPanel ? "…" : `${(niveaux || []).length} classe${(niveaux || []).length > 1 ? "s" : ""}`}
                        </span>
                      </div>
                      {loadingPanel ? (
                        <div className="stp-panel-loading"><span className="stp-spinner-dark" /> Chargement…</div>
                      ) : (niveaux || []).length === 0 ? (
                        <div className="stp-panel-empty">Aucun niveau configuré pour cet établissement.</div>
                      ) : (
                        <ul className="stp-list">
                          {niveaux.map(n => (
                            <li key={n.id} className="stp-list-row stp-list-row-preview">
                              <i className="ti ti-corner-down-right stp-row-icon" />
                              <span className="stp-row-name">{n.nom}</span>
                              <span className="stp-row-tag">{n.cycle_display}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}

                  {isManageOpen && (e.cle === "classes" || e.cle === "matieres") && (
                    <ManagePanel
                      cle={e.cle}
                      loading={loadingPanel}
                      list={items[e.cle] || []}
                      editingId={editingId} editNom={editNom} editCoefficient={editCoefficient} savingId={savingId}
                      onEditNomChange={setEditNom} onEditCoefficientChange={setEditCoefficient}
                      onStartEdit={startEdit} onCancelEdit={cancelEdit} onSaveEdit={(id) => saveEdit(e.cle, id)}
                      deletingId={deletingId} deleteBusyId={deleteBusyId}
                      onAskDelete={askDelete} onCancelDelete={cancelDelete} onConfirmDelete={(id) => confirmDelete(e.cle, id)}
                      addOpen={addOpen} onOpenAdd={openAdd} onCancelAdd={cancelAdd}
                      newName={newName} onNewNameChange={setNewName}
                      newNiveauId={newNiveauId} onNewNiveauIdChange={setNewNiveauId}
                      newCoefficient={newCoefficient} onNewCoefficientChange={setNewCoefficient}
                      niveaux={niveaux} adding={adding} onSubmitAdd={() => submitAdd(e.cle)}
                    />
                  )}

                  {isManageOpen && e.cle === "annee_scolaire" && (
                    <AnneeScolairePanel
                      loading={loadingPanel}
                      list={items.annee_scolaire || []}
                      addOpen={aeAddOpen} onOpenAdd={openAeAdd} onCancelAdd={cancelAeAdd}
                      nom={aeNom} onNomChange={setAeNom}
                      dateDebut={aeDateDebut} onDateDebutChange={setAeDateDebut}
                      dateFin={aeDateFin} onDateFinChange={setAeDateFin}
                      saving={aeSaving} onSubmitAdd={submitAeAdd}
                      activatingId={aeActivatingId} onActivate={handleActivateAnnee}
                      editingId={aeEditingId}
                      editNom={aeEditNom} onEditNomChange={setAeEditNom}
                      editDateDebut={aeEditDateDebut} onEditDateDebutChange={setAeEditDateDebut}
                      editDateFin={aeEditDateFin} onEditDateFinChange={setAeEditDateFin}
                      editSaving={aeEditSaving}
                      onStartEdit={startAeEdit} onCancelEdit={cancelAeEdit} onSaveEdit={saveAeEdit}
                      deletingId={aeDeletingId} deleteBusyId={aeDeleteBusyId}
                      deleteChecking={aeDeleteChecking} deleteStats={aeDeleteStats}
                      onAskDelete={askAeDelete} onCancelDelete={cancelAeDelete} onConfirmDelete={confirmAeDelete}
                      archivingId={aeArchivingId} onArchive={handleAeArchive}
                      restoringId={aeRestoringId} onRestore={handleAeRestore}
                    />
                  )}

                  {isManageOpen && e.cle === "etablissement" && (
                    <EtablissementPanel
                      loading={loadingPanel}
                      detail={etabDetail}
                      editOpen={etabEditOpen}
                      onOpenEdit={() => setEtabEditOpen(true)}
                      onCancelEdit={() => {
                        setEtabEditOpen(false);
                        setEtabLogoFile(null); setEtabCachetFile(null); setEtabLogoPaysFile(null);
                        if (etabDetail) {
                          setEtabLogoPreview(etabDetail.logo ? getImageUrl(etabDetail.logo) : null);
                          setEtabCachetPreview(etabDetail.cachet ? getImageUrl(etabDetail.cachet) : null);
                          setEtabLogoPaysPreview(etabDetail.logo_pays ? getImageUrl(etabDetail.logo_pays) : null);
                        }
                      }}
                      form={etabForm}
                      onFormChange={(field, value) => setEtabForm(prev => ({ ...prev, [field]: value }))}
                      onFileChange={handleEtabFileChange}
                      logoPreview={etabLogoPreview}
                      cachetPreview={etabCachetPreview}
                      logoPaysPreview={etabLogoPaysPreview}
                      saving={etabSaving}
                      onSave={saveEtablissement}
                    />
                  )}

                  {isManageOpen && e.cle === "carte" && (
                    <CartePanel
                      loading={loadingPanel}
                      list={items.carte || []}
                      annees={items.annee_scolaire || []}
                      addOpen={carteAddOpen} editingId={carteEditingId}
                      form={carteForm}
                      onFormChange={(field, value) => setCarteForm(prev => ({ ...prev, [field]: value }))}
                      onOpenAdd={openCarteAdd} onCancelAdd={cancelCarteAdd}
                      onStartEdit={startCarteEdit} onCancelEdit={cancelCarteEdit}
                      saving={carteSaving} onSubmit={submitCarteForm}
                      deletingId={carteDeletingId} deleteBusyId={carteDeleteBusyId}
                      onAskDelete={askDeleteCarte} onCancelDelete={cancelDeleteCarte} onConfirmDelete={confirmDeleteCarte}
                      activatingId={carteActivatingId} onActivate={activateCarte}
                    />
                  )}

                  {isManageOpen && e.cle === "bulletin" && (
                    <BulletinPanel
                      loading={loadingPanel}
                      list={items.bulletin || []}
                      annees={items.annee_scolaire || []}
                      cycles={cyclesEtab || []}
                      addOpen={bulletinAddOpen} editingId={bulletinEditingId}
                      form={bulletinForm}
                      onFormChange={(field, value) => setBulletinForm(prev => ({ ...prev, [field]: value }))}
                      rowsEducateur={bulletinRowsEducateur} setRowsEducateur={setBulletinRowsEducateur}
                      rowsEcole={bulletinRowsEcole} setRowsEcole={setBulletinRowsEcole}
                      onOpenAdd={openBulletinAdd} onCancelAdd={cancelBulletinAdd}
                      onStartEdit={startBulletinEdit} onCancelEdit={cancelBulletinEdit}
                      saving={bulletinSaving} onSubmit={submitBulletinForm}
                      deletingId={bulletinDeletingId} deleteBusyId={bulletinDeleteBusyId}
                      onAskDelete={askDeleteBulletin} onCancelDelete={cancelDeleteBulletin} onConfirmDelete={confirmDeleteBulletin}
                      activatingId={bulletinActivatingId} onActivate={activateBulletin}
                    />
                  )}
                </div>
              );
            })}
          </div>

          <div className="stp-footer">
            <span className="stp-footer-hint">
              <i className="ti ti-info-circle" />
              {etapesActives.length > 0
                ? "Ce message reste affiché tant que la configuration n'est pas terminée."
                : "Toutes les étapes automatiques sont prêtes."}
            </span>
            <button type="button" className="stp-dismiss" onClick={handleMasquer}>
              Masquer pour cette session
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .stp-wrap { margin-bottom: 20px; animation: stpSlideIn .45s cubic-bezier(0.16, 1, 0.3, 1); }
        .stp-wrap.stp-closing { animation: stpSlideOut .28s ease forwards; }
        @keyframes stpSlideIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes stpSlideOut { to { opacity: 0; transform: translateY(-8px) scale(0.99); } }

        .stp-card {
          position: relative; border-radius: 16px;
          background: linear-gradient(180deg, #FFFDF7 0%, #FFFFFF 60%);
          border: 1px solid rgba(217, 119, 6, 0.22);
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 8px 24px -12px rgba(217, 119, 6, 0.18);
          overflow: hidden;
        }
        .stp-glow {
          position: absolute; top: -40%; right: -10%; width: 280px; height: 280px;
          background: radial-gradient(circle, rgba(245, 158, 11, 0.12) 0%, transparent 70%);
          pointer-events: none; animation: stpPulseGlow 4s ease-in-out infinite;
        }
        @keyframes stpPulseGlow { 0%, 100% { opacity: 0.6; transform: scale(1); } 50% { opacity: 1; transform: scale(1.15); } }

        .stp-header {
          position: relative; z-index: 1; width: 100%; display: flex; align-items: center;
          justify-content: space-between; gap: 16px; padding: 16px 18px; background: none;
          border: none; cursor: pointer; text-align: left; font-family: inherit;
        }
        .stp-header-left { display: flex; align-items: center; gap: 14px; min-width: 0; }
        .stp-icon {
          position: relative; flex-shrink: 0; width: 40px; height: 40px; border-radius: 12px;
          background: linear-gradient(135deg, #F59E0B, #D97706); color: #fff; display: flex;
          align-items: center; justify-content: center; font-size: 18px;
          box-shadow: 0 4px 14px rgba(217, 119, 6, 0.32);
        }
        .stp-icon-ping {
          position: absolute; top: -3px; right: -3px; width: 10px; height: 10px;
          border-radius: 50%; background: #EF4444; border: 2px solid #FFFDF7;
        }
        .stp-icon-ping::after {
          content: ""; position: absolute; inset: 0; border-radius: 50%; background: #EF4444;
          animation: stpPing 1.8s cubic-bezier(0, 0, 0.2, 1) infinite;
        }
        @keyframes stpPing { 0% { transform: scale(1); opacity: 0.7; } 75%, 100% { transform: scale(2.4); opacity: 0; } }

        .stp-header-text { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
        .stp-title-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .stp-title { font-size: 15px; font-weight: 700; color: #0F172A; letter-spacing: -0.01em; }
        .stp-badge {
          font-size: 11px; font-weight: 700; color: #B45309; background: rgba(245, 158, 11, 0.14);
          padding: 2px 9px; border-radius: 999px; white-space: nowrap;
        }
        .stp-subtitle {
          font-size: 12.5px; color: #64748B; font-weight: 500; overflow: hidden;
          text-overflow: ellipsis; white-space: nowrap; max-width: 480px;
        }
        .stp-header-right { display: flex; align-items: center; gap: 14px; flex-shrink: 0; }
        .stp-progress-ring { position: relative; width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; }
        .stp-ring-svg { width: 40px; height: 40px; transform: rotate(-90deg); }
        .stp-ring-track { fill: none; stroke: rgba(15, 23, 42, 0.08); stroke-width: 4; }
        .stp-ring-fill { fill: none; stroke: #D97706; stroke-width: 4; stroke-linecap: round; transition: stroke-dashoffset .5s cubic-bezier(0.16, 1, 0.3, 1); }
        .stp-ring-label { position: absolute; font-size: 10px; font-weight: 800; color: #0F172A; }
        .stp-chevron { font-size: 18px; color: #94A3B8; transition: transform .3s ease; }
        .stp-chevron-collapsed { transform: rotate(-90deg); }

        .stp-bar-track { position: relative; height: 4px; background: rgba(15, 23, 42, 0.06); overflow: hidden; }
        .stp-bar-fill { position: relative; height: 100%; background: linear-gradient(90deg, #F59E0B, #D97706); transition: width .5s cubic-bezier(0.16, 1, 0.3, 1); overflow: hidden; }
        .stp-bar-shimmer { position: absolute; inset: 0; background: linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent); animation: stpShimmer 2s ease-in-out infinite; }
        @keyframes stpShimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }

        .stp-body { position: relative; z-index: 1; display: block; transition: max-height .32s cubic-bezier(0.16, 1, 0.3, 1); }
        .stp-body-collapsed { max-height: 0; overflow: hidden; }
        .stp-body:not(.stp-body-collapsed) { max-height: 4000px; }
        .stp-steps { padding: 4px 18px 6px; }
        .stp-step-block { border-top: 1px solid rgba(15, 23, 42, 0.05); }
        .stp-step-block:first-child { border-top: none; }
        .stp-step { display: flex; align-items: center; gap: 12px; padding: 10px 4px; animation: stpStepIn .4s cubic-bezier(0.16, 1, 0.3, 1) backwards; animation-delay: calc(var(--stp-i) * 0.06s); flex-wrap: wrap; }
        @keyframes stpStepIn { from { opacity: 0; transform: translateX(-6px); } to { opacity: 1; transform: translateX(0); } }
        .stp-check { flex-shrink: 0; width: 22px; height: 22px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 13px; }
        .stp-step-done .stp-check { background: #DCFCE7; color: #16A34A; }
        .stp-step-pending .stp-check { background: rgba(217, 119, 6, 0.1); border: 1.5px dashed rgba(217, 119, 6, 0.4); }
        .stp-check-dot { width: 6px; height: 6px; border-radius: 50%; background: #D97706; }
        .stp-step-titre { flex: 1; min-width: 0; font-size: 13.5px; font-weight: 500; color: #334155; }
        .stp-step-done .stp-step-titre { color: #94A3B8; text-decoration: line-through; text-decoration-color: rgba(148, 163, 184, 0.5); }
        .stp-done-tag { font-size: 11px; font-weight: 700; color: #16A34A; background: #DCFCE7; padding: 2px 9px; border-radius: 999px; }

        .stp-cta {
          display: inline-flex; align-items: center; gap: 6px; padding: 7px 14px; border-radius: 8px;
          border: none; background: linear-gradient(135deg, #F59E0B, #D97706); color: #fff; font-size: 12.5px;
          font-weight: 600; font-family: inherit; cursor: pointer; white-space: nowrap;
          box-shadow: 0 2px 8px rgba(217, 119, 6, 0.28); transition: transform .15s ease, box-shadow .15s ease;
        }
        .stp-cta:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(217, 119, 6, 0.38); }
        .stp-cta:disabled { opacity: 0.6; cursor: default; transform: none; }
        .stp-cta i { font-size: 14px; }
        .stp-cta-ghost { background: rgba(217, 119, 6, 0.08); color: #B45309; box-shadow: none; }
        .stp-cta-ghost:hover:not(:disabled) { background: rgba(217, 119, 6, 0.14); box-shadow: none; }
        .stp-cta-sm { padding: 5px 10px; font-size: 12px; }
        .stp-cta-outline { background: #fff; color: #64748B; border: 1px solid rgba(15, 23, 42, 0.12); box-shadow: none; }
        .stp-cta-outline:hover:not(:disabled) { background: #F8FAFC; box-shadow: none; }
        .stp-cta-danger { background: #DC2626; box-shadow: 0 2px 8px rgba(220, 38, 38, 0.28); }
        .stp-cta-danger:hover:not(:disabled) { background: #B91C1C; box-shadow: 0 4px 12px rgba(220, 38, 38, 0.34); }

        .stp-spinner { width: 13px; height: 13px; border-radius: 50%; border: 2px solid rgba(255,255,255,0.4); border-top-color: #fff; animation: stpSpin .7s linear infinite; display: inline-block; }
        .stp-spinner-dark { width: 13px; height: 13px; border-radius: 50%; border: 2px solid rgba(217, 119, 6, 0.2); border-top-color: #D97706; animation: stpSpin .7s linear infinite; display: inline-block; }
        @keyframes stpSpin { to { transform: rotate(360deg); } }

        .stp-footer { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 10px 18px 16px; flex-wrap: wrap; }
        .stp-footer-hint { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #94A3B8; font-weight: 500; }
        .stp-footer-hint i { font-size: 14px; color: #CBD5E1; }
        .stp-dismiss {
          background: none; border: none; font-family: inherit; font-size: 12px; font-weight: 600; color: #94A3B8;
          cursor: pointer; padding: 4px 2px; text-decoration: underline; text-decoration-color: transparent;
          transition: color .15s ease, text-decoration-color .15s ease;
        }
        .stp-dismiss:hover { color: #64748B; text-decoration-color: #64748B; }

        .stp-panel {
          margin: 0 4px 12px 34px; background: #fff; border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 10px; box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04); overflow: hidden;
          animation: stpPanelIn .2s ease;
        }
        @keyframes stpPanelIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
        .stp-panel-head { display: flex; align-items: center; justify-content: space-between; padding: 10px 14px; background: #F8FAFC; border-bottom: 1px solid rgba(15, 23, 42, 0.06); }
        .stp-panel-title { font-size: 12.5px; font-weight: 700; color: #334155; }
        .stp-panel-count { font-size: 11px; font-weight: 600; color: #94A3B8; }
        .stp-panel-loading, .stp-panel-empty { padding: 16px 14px; font-size: 12.5px; color: #94A3B8; display: flex; align-items: center; gap: 8px; }
        .stp-panel-empty { font-style: italic; }
        .stp-panel-body { padding: 14px; display: flex; flex-direction: column; gap: 10px; }

        .stp-list { list-style: none; margin: 0; padding: 0; max-height: 280px; overflow-y: auto; }
        .stp-list-row { display: flex; align-items: center; gap: 8px; padding: 9px 14px; border-top: 1px solid rgba(15, 23, 42, 0.05); font-size: 13px; flex-wrap: wrap; }
        .stp-list-row:first-child { border-top: none; }
        .stp-list-row-preview { color: #92400E; }
        .stp-row-icon { font-size: 13px; color: #D97706; flex-shrink: 0; }
        .stp-row-name { flex: 1; min-width: 0; color: #334155; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .stp-row-tag { font-size: 11px; font-weight: 600; color: #94A3B8; background: #F1F5F9; padding: 2px 8px; border-radius: 999px; flex-shrink: 0; }
        .stp-row-tag-active { color: #16A34A; background: #DCFCE7; }
        .stp-row-tag-archived { color: #94A3B8; background: #F1F5F9; }
        .stp-row-actions { display: flex; align-items: center; gap: 2px; flex-shrink: 0; }
        .stp-row-btn {
          width: 26px; height: 26px; border-radius: 6px; border: none; background: transparent; color: #94A3B8;
          cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 13px;
          transition: background .15s ease, color .15s ease;
        }
        .stp-row-btn:hover { background: rgba(217, 119, 6, 0.1); color: #D97706; }
        .stp-row-btn-danger:hover { background: rgba(220, 38, 38, 0.1); color: #DC2626; }
        .stp-row-btn:disabled { opacity: 0.5; cursor: default; }

        .stp-list-row-editing { padding: 8px 14px; background: rgba(217, 119, 6, 0.04); }
        .stp-edit-form { display: flex; align-items: center; gap: 6px; width: 100%; flex-wrap: wrap; }
        .stp-edit-input { flex: 1; min-width: 0; border: 1px solid #D97706; border-radius: 6px; padding: 6px 9px; font-size: 13px; font-family: inherit; background: #fff; color: #0F172A; }
        .stp-edit-coef { width: 68px; flex: 0 0 auto; border: 1px solid #D97706; border-radius: 6px; padding: 6px 9px; font-size: 13px; font-family: inherit; background: #fff; color: #0F172A; text-align: center; }
        .stp-edit-coef-label { font-size: 11px; color: #94A3B8; flex-shrink: 0; }
        .stp-edit-input:focus, .stp-edit-coef:focus { outline: none; box-shadow: 0 0 0 3px rgba(217, 119, 6, 0.12); }
        .stp-edit-date { flex: 0 0 132px; border: 1px solid #D97706; border-radius: 6px; padding: 6px 9px; font-size: 12.5px; font-family: inherit; background: #fff; color: #0F172A; }
        .stp-edit-date:focus { outline: none; box-shadow: 0 0 0 3px rgba(217, 119, 6, 0.12); }

        .stp-icon-btn { flex-shrink: 0; width: 28px; height: 28px; border-radius: 6px; border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 14px; transition: filter .15s ease; }
        .stp-icon-btn:disabled { opacity: 0.6; cursor: default; }
        .stp-icon-btn-confirm { background: #16A34A; color: #fff; }
        .stp-icon-btn-confirm:hover:not(:disabled) { filter: brightness(1.08); }
        .stp-icon-btn-cancel { background: #F1F5F9; color: #64748B; }
        .stp-icon-btn-cancel:hover:not(:disabled) { background: #E2E8F0; }

        .stp-list-row-deleting { padding: 9px 14px; background: rgba(220, 38, 38, 0.05); }
        .stp-delete-confirm { display: flex; align-items: center; gap: 8px; width: 100%; flex-wrap: wrap; }
        .stp-delete-confirm-text { flex: 1; font-size: 12.5px; color: #991B1B; font-weight: 500; }
        .stp-delete-confirm-actions { display: flex; gap: 6px; flex-shrink: 0; }

        .stp-list-row-warn { padding: 9px 14px; background: rgba(245, 158, 11, 0.07); }
        .stp-warn-block { display: flex; flex-direction: column; gap: 8px; width: 100%; }
        .stp-warn-text { font-size: 12.5px; color: #92400E; font-weight: 500; }
        .stp-warn-stats { display: flex; flex-wrap: wrap; gap: 6px; }
        .stp-warn-stat { font-size: 11px; font-weight: 600; color: #92400E; background: rgba(245, 158, 11, 0.14); padding: 2px 8px; border-radius: 999px; }
        .stp-warn-actions { display: flex; gap: 6px; }

        .stp-panel-footer { padding: 10px 14px; border-top: 1px solid rgba(15, 23, 42, 0.06); background: #FAFAFA; }
        .stp-add-trigger { display: flex; align-items: center; gap: 6px; width: 100%; padding: 7px 0; background: none; border: none; font-family: inherit; font-size: 12.5px; font-weight: 600; color: #D97706; cursor: pointer; }
        .stp-add-trigger:disabled { color: #CBD5E1; cursor: default; }
        .stp-add-trigger i { font-size: 15px; }
        .stp-add-form { display: flex; flex-direction: column; gap: 8px; }
        .stp-add-form-label { font-size: 11.5px; font-weight: 700; color: #64748B; text-transform: uppercase; letter-spacing: 0.03em; }
        .stp-add-form-row { display: flex; gap: 8px; flex-wrap: wrap; }
        .stp-add-input, .stp-add-select, .stp-add-coef { border: 1px solid rgba(15, 23, 42, 0.14); border-radius: 6px; padding: 7px 9px; font-size: 13px; font-family: inherit; background: #fff; color: #0F172A; }
        .stp-add-input { flex: 1; min-width: 0; }
        .stp-add-select { flex: 0 0 140px; }
        .stp-add-coef { flex: 0 0 76px; text-align: center; }
        .stp-add-input:focus, .stp-add-select:focus, .stp-add-coef:focus { outline: none; border-color: #D97706; box-shadow: 0 0 0 3px rgba(217, 119, 6, 0.1); }
        .stp-add-form-actions { display: flex; justify-content: flex-end; gap: 8px; }

        .stp-field { display: flex; flex-direction: column; gap: 4px; }
        .stp-field-label { font-size: 11.5px; font-weight: 700; color: #64748B; text-transform: uppercase; letter-spacing: 0.03em; }
        .stp-field-value { font-size: 13.5px; color: #334155; word-break: break-word; }
        .stp-field-value.stp-field-empty { color: #CBD5E1; font-style: italic; }
        .stp-field-input { border: 1px solid rgba(15, 23, 42, 0.14); border-radius: 6px; padding: 7px 9px; font-size: 13px; font-family: inherit; background: #fff; color: #0F172A; }
        .stp-field-input:focus { outline: none; border-color: #D97706; box-shadow: 0 0 0 3px rgba(217, 119, 6, 0.1); }
        .stp-field-textarea { border: 1px solid rgba(15, 23, 42, 0.14); border-radius: 6px; padding: 7px 9px; font-size: 13px; font-family: inherit; background: #fff; color: #0F172A; resize: vertical; }
        .stp-field-textarea:focus { outline: none; border-color: #D97706; box-shadow: 0 0 0 3px rgba(217, 119, 6, 0.1); }
        .stp-field-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }

        .stp-appr-row { display: flex; gap: 6px; align-items: center; }
        .stp-appr-seuil { width: 64px; flex: 0 0 auto; border: 1px solid rgba(15, 23, 42, 0.14); border-radius: 6px; padding: 6px 8px; font-size: 12.5px; font-family: inherit; background: #fff; color: #0F172A; text-align: center; }
        .stp-appr-texte { flex: 1; min-width: 0; border: 1px solid rgba(15, 23, 42, 0.14); border-radius: 6px; padding: 6px 8px; font-size: 12.5px; font-family: inherit; background: #fff; color: #0F172A; }
        .stp-appr-seuil:focus, .stp-appr-texte:focus { outline: none; border-color: #D97706; box-shadow: 0 0 0 3px rgba(217, 119, 6, 0.1); }

        .stp-etab-images { display: flex; gap: 18px; flex-wrap: wrap; }
        .stp-etab-img-block { display: flex; flex-direction: column; align-items: center; gap: 6px; }
        .stp-etab-img { width: 84px; height: 84px; object-fit: contain; border-radius: 8px; border: 1px solid rgba(15, 23, 42, 0.1); background: #F8FAFC; }
        .stp-etab-img-placeholder { width: 84px; height: 84px; border-radius: 8px; border: 1px dashed rgba(15, 23, 42, 0.15); background: #F8FAFC; display: flex; align-items: center; justify-content: center; color: #CBD5E1; font-size: 26px; }
        .stp-etab-img-label { font-size: 11px; font-weight: 600; color: #94A3B8; }
        .stp-etab-file-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .stp-etab-file-row input[type="file"] { font-size: 12px; max-width: 180px; }
        .stp-etab-img-sm { width: 44px; height: 44px; object-fit: contain; border-radius: 6px; border: 1px solid rgba(15, 23, 42, 0.1); background: #F8FAFC; flex-shrink: 0; }
        .stp-etab-info-box { font-size: 11.5px; color: #94A3B8; display: flex; flex-wrap: wrap; gap: 14px; padding: 10px 12px; background: #F8FAFC; border-radius: 8px; border: 1px solid rgba(15, 23, 42, 0.06); }
        .stp-etab-info-box strong { color: #64748B; }

        @media (max-width: 640px) {
          .stp-subtitle { display: none; }
          .stp-header-right { gap: 8px; }
          .stp-title-row { gap: 6px; }
          .stp-panel { margin-left: 4px; }
          .stp-add-form-row { flex-direction: column; }
          .stp-add-select, .stp-add-coef { flex: 1 1 auto; }
          .stp-field-grid { grid-template-columns: 1fr; }
          .stp-etab-images { gap: 10px; }
        }
        @media (prefers-reduced-motion: reduce) {
          .stp-wrap, .stp-glow, .stp-icon-ping::after, .stp-bar-shimmer, .stp-step { animation: none !important; }
        }
      `}</style>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// Panneau : classes / matières (liste + édition + suppression + ajout)
// ════════════════════════════════════════════════════════════
function ManagePanel({
  cle, loading, list,
  editingId, editNom, editCoefficient, savingId,
  onEditNomChange, onEditCoefficientChange, onStartEdit, onCancelEdit, onSaveEdit,
  deletingId, deleteBusyId, onAskDelete, onCancelDelete, onConfirmDelete,
  addOpen, onOpenAdd, onCancelAdd,
  newName, onNewNameChange,
  newNiveauId, onNewNiveauIdChange,
  newCoefficient, onNewCoefficientChange,
  niveaux, adding, onSubmitAdd,
}) {
  const isMatiere = cle === "matieres";
  const label = isMatiere ? "matière" : "classe";
  const labelPlural = isMatiere ? "matières" : "classes";

  return (
    <div className="stp-panel">
      <div className="stp-panel-head">
        <span className="stp-panel-title">{isMatiere ? "Matières de l'année en cours" : "Classes de l'année en cours"}</span>
        <span className="stp-panel-count">{loading ? "…" : `${list.length} ${list.length > 1 ? labelPlural : label}`}</span>
      </div>

      {loading ? (
        <div className="stp-panel-loading"><span className="stp-spinner-dark" /> Chargement…</div>
      ) : (
        <ul className="stp-list">
          {list.length === 0 && <li className="stp-panel-empty">Aucune {label} pour le moment.</li>}

          {list.map(item => {
            const isEditing = editingId === item.id;
            const isDeleting = deletingId === item.id;
            const isSaving = savingId === item.id;
            const isDeleteBusy = deleteBusyId === item.id;

            if (isEditing) {
              return (
                <li key={item.id} className="stp-list-row stp-list-row-editing">
                  <form className="stp-edit-form" onSubmit={(ev) => { ev.preventDefault(); onSaveEdit(item.id); }}>
                    <input className="stp-edit-input" value={editNom} autoFocus onChange={(ev) => onEditNomChange(ev.target.value)}
                      onKeyDown={(ev) => { if (ev.key === "Escape") onCancelEdit(); }} disabled={isSaving} />
                    {isMatiere && (
                      <>
                        <span className="stp-edit-coef-label">coef</span>
                        <input type="number" min="1" className="stp-edit-coef" value={editCoefficient}
                          onChange={(ev) => onEditCoefficientChange(ev.target.value)} disabled={isSaving} />
                      </>
                    )}
                    <button type="submit" className="stp-icon-btn stp-icon-btn-confirm" disabled={isSaving} title="Enregistrer">
                      {isSaving ? <span className="stp-spinner" /> : <i className="ti ti-check" />}
                    </button>
                    <button type="button" className="stp-icon-btn stp-icon-btn-cancel" onClick={onCancelEdit} disabled={isSaving} title="Annuler">
                      <i className="ti ti-x" />
                    </button>
                  </form>
                </li>
              );
            }

            if (isDeleting) {
              return (
                <li key={item.id} className="stp-list-row stp-list-row-deleting">
                  <div className="stp-delete-confirm">
                    <span className="stp-delete-confirm-text">Supprimer « {item.nom} » ?</span>
                    <div className="stp-delete-confirm-actions">
                      <button type="button" className="stp-cta stp-cta-outline stp-cta-sm" onClick={onCancelDelete} disabled={isDeleteBusy}>Annuler</button>
                      <button type="button" className="stp-cta stp-cta-danger stp-cta-sm" onClick={() => onConfirmDelete(item.id)} disabled={isDeleteBusy}>
                        {isDeleteBusy ? <span className="stp-spinner" /> : "Supprimer"}
                      </button>
                    </div>
                  </div>
                </li>
              );
            }

            return (
              <li key={item.id} className="stp-list-row">
                <span className="stp-row-name">{item.nom}</span>
                {cle === "classes" && item.niveau_nom && <span className="stp-row-tag">{item.niveau_nom}</span>}
                {isMatiere && item.coefficient != null && <span className="stp-row-tag">coef {item.coefficient}</span>}
                <div className="stp-row-actions">
                  <button type="button" className="stp-row-btn" onClick={() => onStartEdit(item)} title="Modifier"><i className="ti ti-pencil" /></button>
                  <button type="button" className="stp-row-btn stp-row-btn-danger" onClick={() => onAskDelete(item)} title="Supprimer"><i className="ti ti-trash" /></button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div className="stp-panel-footer">
        {!addOpen ? (
          <button type="button" className="stp-add-trigger" onClick={onOpenAdd}>
            <i className="ti ti-plus" />Ajouter {isMatiere ? "une matière" : "une classe"}
          </button>
        ) : (
          <form className="stp-add-form" onSubmit={(ev) => { ev.preventDefault(); onSubmitAdd(); }}>
            <span className="stp-add-form-label">Nouvelle {label}</span>
            <div className="stp-add-form-row">
              {cle === "classes" && (
                <select className="stp-add-select" value={newNiveauId} onChange={(ev) => onNewNiveauIdChange(ev.target.value)} disabled={adding}>
                  <option value="">Niveau...</option>
                  {(niveaux || []).map(n => <option key={n.id} value={n.id}>{n.nom}</option>)}
                </select>
              )}
              <input className="stp-add-input" placeholder={cle === "classes" ? "Ex: CM2 B" : "Ex: Éducation civique"}
                value={newName} autoFocus onChange={(ev) => onNewNameChange(ev.target.value)} disabled={adding} />
              {isMatiere && (
                <input type="number" min="1" className="stp-add-coef" placeholder="Coef" value={newCoefficient}
                  onChange={(ev) => onNewCoefficientChange(ev.target.value)} disabled={adding} />
              )}
            </div>
            <div className="stp-add-form-actions">
              <button type="button" className="stp-cta stp-cta-outline stp-cta-sm" onClick={onCancelAdd} disabled={adding}>Annuler</button>
              <button type="submit" className="stp-cta stp-cta-sm" disabled={adding}>{adding ? <span className="stp-spinner" /> : "Ajouter"}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// Panneau : années scolaires
// (liste + activation + création + édition + archivage + restauration
//  + suppression avec vérification "année non vide")
// ════════════════════════════════════════════════════════════
function AnneeScolairePanel({
  loading, list,
  addOpen, onOpenAdd, onCancelAdd,
  nom, onNomChange, dateDebut, onDateDebutChange, dateFin, onDateFinChange,
  saving, onSubmitAdd,
  activatingId, onActivate,
  editingId, editNom, onEditNomChange, editDateDebut, onEditDateDebutChange,
  editDateFin, onEditDateFinChange, editSaving, onStartEdit, onCancelEdit, onSaveEdit,
  deletingId, deleteBusyId, deleteChecking, deleteStats,
  onAskDelete, onCancelDelete, onConfirmDelete,
  archivingId, onArchive,
  restoringId, onRestore,
}) {
  const formatDate = (d) => {
    if (!d) return "—";
    try { return new Date(d).toLocaleDateString("fr-FR"); } catch { return d; }
  };

  return (
    <div className="stp-panel">
      <div className="stp-panel-head">
        <span className="stp-panel-title">Années scolaires</span>
        <span className="stp-panel-count">{loading ? "…" : `${list.length} année${list.length > 1 ? "s" : ""}`}</span>
      </div>

      {loading ? (
        <div className="stp-panel-loading"><span className="stp-spinner-dark" /> Chargement…</div>
      ) : (
        <ul className="stp-list">
          {list.length === 0 && <li className="stp-panel-empty">Aucune année scolaire pour le moment.</li>}

          {list.map(a => {
            const isEditing = editingId === a.id;
            const isDeleting = deletingId === a.id;
            const isDeleteBusy = deleteBusyId === a.id;
            const isArchiving = archivingId === a.id;
            const isRestoring = restoringId === a.id;

            if (isEditing) {
              return (
                <li key={a.id} className="stp-list-row stp-list-row-editing">
                  <form className="stp-edit-form" onSubmit={(ev) => { ev.preventDefault(); onSaveEdit(a.id); }}>
                    <input className="stp-edit-input" style={{ flex: "1 1 140px" }} value={editNom} autoFocus
                      onChange={(ev) => onEditNomChange(ev.target.value)}
                      onKeyDown={(ev) => { if (ev.key === "Escape") onCancelEdit(); }} disabled={editSaving} placeholder="Ex: 2026-2027" />
                    <input type="date" className="stp-edit-date" value={editDateDebut}
                      onChange={(ev) => onEditDateDebutChange(ev.target.value)} disabled={editSaving} />
                    <input type="date" className="stp-edit-date" value={editDateFin}
                      onChange={(ev) => onEditDateFinChange(ev.target.value)} disabled={editSaving} />
                    <button type="submit" className="stp-icon-btn stp-icon-btn-confirm" disabled={editSaving} title="Enregistrer">
                      {editSaving ? <span className="stp-spinner" /> : <i className="ti ti-check" />}
                    </button>
                    <button type="button" className="stp-icon-btn stp-icon-btn-cancel" onClick={onCancelEdit} disabled={editSaving} title="Annuler">
                      <i className="ti ti-x" />
                    </button>
                  </form>
                </li>
              );
            }

            if (isDeleting) {
              if (deleteChecking) {
                return (
                  <li key={a.id} className="stp-list-row-warn">
                    <div className="stp-panel-loading" style={{ padding: "6px 0" }}>
                      <span className="stp-spinner-dark" /> Vérification des données de l'année…
                    </div>
                  </li>
                );
              }
              if (deleteStats && deleteStats.peut_supprimer === false) {
                const entries = Object.entries(deleteStats.statistiques || {}).filter(([, v]) => v > 0);
                return (
                  <li key={a.id} className="stp-list-row stp-list-row-warn">
                    <div className="stp-warn-block">
                      <span className="stp-warn-text">
                        <i className="ti ti-alert-triangle" /> « {a.nom} » contient des données et ne peut pas être supprimée.
                      </span>
                      {entries.length > 0 && (
                        <div className="stp-warn-stats">
                          {entries.map(([k, v]) => (
                            <span key={k} className="stp-warn-stat">{k.replace(/_/g, " ")} : {v}</span>
                          ))}
                        </div>
                      )}
                      <div className="stp-warn-actions">
                        <button type="button" className="stp-cta stp-cta-outline stp-cta-sm" onClick={onCancelDelete}>Annuler</button>
                        <button type="button" className="stp-cta stp-cta-sm" onClick={() => onArchive(a.id)} disabled={isArchiving}>
                          {isArchiving ? <span className="stp-spinner" /> : <><i className="ti ti-archive" />Archiver à la place</>}
                        </button>
                      </div>
                    </div>
                  </li>
                );
              }
              return (
                <li key={a.id} className="stp-list-row stp-list-row-deleting">
                  <div className="stp-delete-confirm">
                    <span className="stp-delete-confirm-text">Supprimer l'année « {a.nom} » ?</span>
                    <div className="stp-delete-confirm-actions">
                      <button type="button" className="stp-cta stp-cta-outline stp-cta-sm" onClick={onCancelDelete} disabled={isDeleteBusy}>Annuler</button>
                      <button type="button" className="stp-cta stp-cta-danger stp-cta-sm" onClick={() => onConfirmDelete(a.id)} disabled={isDeleteBusy}>
                        {isDeleteBusy ? <span className="stp-spinner" /> : "Supprimer"}
                      </button>
                    </div>
                  </div>
                </li>
              );
            }

            return (
              <li key={a.id} className="stp-list-row">
                <span className="stp-row-name">{a.nom}</span>
                <span className="stp-row-tag" style={{ fontWeight: 500 }}>{formatDate(a.date_debut)} → {formatDate(a.date_fin)}</span>
                <span className={`stp-row-tag ${a.archived ? "stp-row-tag-archived" : a.actif ? "stp-row-tag-active" : ""}`}>
                  {a.archived ? "Archivée" : a.actif ? "Active" : "Inactive"}
                </span>

                {!a.actif && !a.archived && (
                  <button type="button" className="stp-cta stp-cta-ghost stp-cta-sm" onClick={() => onActivate(a.id)} disabled={activatingId === a.id}>
                    {activatingId === a.id ? <span className="stp-spinner" /> : "Activer"}
                  </button>
                )}

                {a.archived && (
                  <button type="button" className="stp-cta stp-cta-ghost stp-cta-sm" onClick={() => onRestore(a.id)} disabled={isRestoring}>
                    {isRestoring ? <span className="stp-spinner" /> : <><i className="ti ti-restore" />Restaurer</>}
                  </button>
                )}

                <div className="stp-row-actions">
                  {!a.archived && (
                    <button type="button" className="stp-row-btn" onClick={() => onStartEdit(a)} title="Modifier"><i className="ti ti-pencil" /></button>
                  )}
                  {!a.actif && !a.archived && (
                    <button type="button" className="stp-row-btn" onClick={() => onArchive(a.id)} disabled={isArchiving} title="Archiver">
                      {isArchiving ? <span className="stp-spinner-dark" /> : <i className="ti ti-archive" />}
                    </button>
                  )}
                  {!a.actif && !a.archived && (
                    <button type="button" className="stp-row-btn stp-row-btn-danger" onClick={() => onAskDelete(a)} title="Supprimer"><i className="ti ti-trash" /></button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div className="stp-panel-footer">
        {!addOpen ? (
          <button type="button" className="stp-add-trigger" onClick={onOpenAdd}>
            <i className="ti ti-plus" />Créer une année scolaire
          </button>
        ) : (
          <form className="stp-add-form" onSubmit={(ev) => { ev.preventDefault(); onSubmitAdd(); }}>
            <span className="stp-add-form-label">Nouvelle année scolaire</span>
            <input className="stp-add-input" placeholder="Ex: 2026-2027" value={nom} autoFocus
              onChange={(ev) => onNomChange(ev.target.value)} disabled={saving} />
            <div className="stp-add-form-row">
              <input type="date" className="stp-add-input" value={dateDebut} onChange={(ev) => onDateDebutChange(ev.target.value)} disabled={saving} />
              <input type="date" className="stp-add-input" value={dateFin} onChange={(ev) => onDateFinChange(ev.target.value)} disabled={saving} />
            </div>
            <div className="stp-add-form-actions">
              <button type="button" className="stp-cta stp-cta-outline stp-cta-sm" onClick={onCancelAdd} disabled={saving}>Annuler</button>
              <button type="submit" className="stp-cta stp-cta-sm" disabled={saving}>{saving ? <span className="stp-spinner" /> : "Créer"}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// Panneau : établissement — tous les champs (infos, coordonnées, images, type/cycles en lecture seule)
// ════════════════════════════════════════════════════════════
function EtablissementPanel({
  loading, detail, editOpen, onOpenEdit, onCancelEdit,
  form, onFormChange, onFileChange,
  logoPreview, cachetPreview, logoPaysPreview,
  saving, onSave,
}) {
  if (loading || !detail) {
    return (
      <div className="stp-panel">
        <div className="stp-panel-loading"><span className="stp-spinner-dark" /> Chargement…</div>
      </div>
    );
  }

  const val = (v) => (v ? v : null);
  const typeLabel = TYPE_ETABLISSEMENT_OPTIONS.find(t => t.value === detail.type_etablissement)?.label || detail.type_etablissement || "—";
  const cyclesLabel = (detail.cycles || []).map(c => CYCLE_LABELS[c] || c).join(", ") || "Aucun";

  return (
    <div className="stp-panel">
      <div className="stp-panel-head">
        <span className="stp-panel-title">Informations de l'établissement</span>
        {!editOpen && (
          <button type="button" className="stp-cta stp-cta-ghost stp-cta-sm" onClick={onOpenEdit}>
            <i className="ti ti-pencil" />Modifier
          </button>
        )}
      </div>

      <div className="stp-panel-body">
        {!editOpen ? (
          <>
            <div className="stp-etab-images">
              <div className="stp-etab-img-block">
                {val(detail.logo) ? <img className="stp-etab-img" src={logoPreview} alt="Logo" /> : <div className="stp-etab-img-placeholder"><i className="ti ti-building" /></div>}
                <span className="stp-etab-img-label">Logo</span>
              </div>
              <div className="stp-etab-img-block">
                {val(detail.cachet) ? <img className="stp-etab-img" src={cachetPreview} alt="Cachet" /> : <div className="stp-etab-img-placeholder"><i className="ti ti-stamp" /></div>}
                <span className="stp-etab-img-label">Cachet</span>
              </div>
              <div className="stp-etab-img-block">
                {val(detail.logo_pays) ? <img className="stp-etab-img" src={logoPaysPreview} alt="Logo pays" /> : <div className="stp-etab-img-placeholder"><i className="ti ti-flag" /></div>}
                <span className="stp-etab-img-label">Logo pays</span>
              </div>
            </div>

            <div className="stp-field-grid">
              <div className="stp-field">
                <span className="stp-field-label">Nom</span>
                <span className="stp-field-value">{detail.nom || "—"}</span>
              </div>
              <div className="stp-field">
                <span className="stp-field-label">Sigle</span>
                <span className="stp-field-value">{detail.sigle || "—"}</span>
              </div>
            </div>

            <div className="stp-field">
              <span className="stp-field-label">Numéro établissement</span>
              <span className={`stp-field-value ${!detail.numero_ecole ? "stp-field-empty" : ""}`}>{detail.numero_ecole || "Non renseigné"}</span>
            </div>

            <div className="stp-field-grid">
              <div className="stp-field">
                <span className="stp-field-label">Email</span>
                <span className={`stp-field-value ${!detail.email ? "stp-field-empty" : ""}`}>{detail.email || "Non renseigné"}</span>
              </div>
              <div className="stp-field">
                <span className="stp-field-label">Téléphone</span>
                <span className={`stp-field-value ${!detail.telephone ? "stp-field-empty" : ""}`}>{detail.telephone || "Non renseigné"}</span>
              </div>
            </div>

            <div className="stp-field">
              <span className="stp-field-label">Site web</span>
              <span className={`stp-field-value ${!detail.site_web ? "stp-field-empty" : ""}`}>{detail.site_web || "Non renseigné"}</span>
            </div>

            <div className="stp-field">
              <span className="stp-field-label">Adresse</span>
              <span className={`stp-field-value ${!detail.adresse ? "stp-field-empty" : ""}`}>{detail.adresse || "Non renseignée"}</span>
            </div>

            <div className="stp-field-grid">
              <div className="stp-field">
                <span className="stp-field-label">Slogan de l'école</span>
                <span className={`stp-field-value ${!detail.slogan ? "stp-field-empty" : ""}`}>{detail.slogan || "Non renseigné"}</span>
              </div>
              <div className="stp-field">
                <span className="stp-field-label">Devise de l'école</span>
                <span className={`stp-field-value ${!detail.devise_ecole ? "stp-field-empty" : ""}`}>{detail.devise_ecole || "Non renseignée"}</span>
              </div>
            </div>

            <div className="stp-field-grid">
              <div className="stp-field">
                <span className="stp-field-label">Pays</span>
                <span className="stp-field-value">{detail.pays || "—"}</span>
              </div>
              <div className="stp-field">
                <span className="stp-field-label">Devise nationale</span>
                <span className="stp-field-value">{detail.devise_pays || "—"}</span>
              </div>
            </div>

            <div className="stp-etab-info-box">
              <span><strong>Type :</strong> {typeLabel}</span>
              <span><strong>Statut :</strong> {detail.active ? "Actif" : "Inactif"}</span>
              <span><strong>Cycles :</strong> {cyclesLabel}</span>
            </div>
          </>
        ) : (
          <form onSubmit={(ev) => { ev.preventDefault(); onSave(); }} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div className="stp-field-grid">
              <div className="stp-field">
                <span className="stp-field-label">Nom *</span>
                <input className="stp-field-input" value={form.nom} onChange={(ev) => onFormChange("nom", ev.target.value)} disabled={saving} />
              </div>
              <div className="stp-field">
                <span className="stp-field-label">Sigle *</span>
                <input className="stp-field-input" value={form.sigle} onChange={(ev) => onFormChange("sigle", ev.target.value.toUpperCase())} disabled={saving} />
              </div>
            </div>

            <div className="stp-field">
              <span className="stp-field-label">Numéro établissement</span>
              <input className="stp-field-input" value={form.numero_ecole} onChange={(ev) => onFormChange("numero_ecole", ev.target.value)} disabled={saving} />
            </div>

            <div className="stp-field-grid">
              <div className="stp-field">
                <span className="stp-field-label">Email</span>
                <input type="email" className="stp-field-input" value={form.email} onChange={(ev) => onFormChange("email", ev.target.value)} disabled={saving} />
              </div>
              <div className="stp-field">
                <span className="stp-field-label">Téléphone</span>
                <input className="stp-field-input" value={form.telephone} onChange={(ev) => onFormChange("telephone", ev.target.value)} disabled={saving} />
              </div>
            </div>

            <div className="stp-field">
              <span className="stp-field-label">Site web</span>
              <input className="stp-field-input" placeholder="https://..." value={form.site_web} onChange={(ev) => onFormChange("site_web", ev.target.value)} disabled={saving} />
            </div>

            <div className="stp-field">
              <span className="stp-field-label">Adresse</span>
              <textarea className="stp-field-textarea" rows={2} value={form.adresse} onChange={(ev) => onFormChange("adresse", ev.target.value)} disabled={saving} />
            </div>

            <div className="stp-field-grid">
              <div className="stp-field">
                <span className="stp-field-label">Slogan de l'école</span>
                <input className="stp-field-input" value={form.slogan} onChange={(ev) => onFormChange("slogan", ev.target.value)} disabled={saving} />
              </div>
              <div className="stp-field">
                <span className="stp-field-label">Devise de l'école</span>
                <input className="stp-field-input" value={form.devise_ecole} onChange={(ev) => onFormChange("devise_ecole", ev.target.value)} disabled={saving} />
              </div>
            </div>

            <div className="stp-field-grid">
              <div className="stp-field">
                <span className="stp-field-label">Pays</span>
                <input className="stp-field-input" value={form.pays} onChange={(ev) => onFormChange("pays", ev.target.value)} disabled={saving} />
              </div>
              <div className="stp-field">
                <span className="stp-field-label">Devise nationale</span>
                <input className="stp-field-input" value={form.devise_pays} onChange={(ev) => onFormChange("devise_pays", ev.target.value)} disabled={saving} />
              </div>
            </div>

            <div className="stp-field">
              <span className="stp-field-label">Logo</span>
              <div className="stp-etab-file-row">
                {logoPreview && <img className="stp-etab-img-sm" src={logoPreview} alt="Logo preview" />}
                <input type="file" accept="image/*" onChange={(ev) => onFileChange("logo", ev.target.files?.[0])} disabled={saving} />
              </div>
            </div>
            <div className="stp-field">
              <span className="stp-field-label">Cachet</span>
              <div className="stp-etab-file-row">
                {cachetPreview && <img className="stp-etab-img-sm" src={cachetPreview} alt="Cachet preview" />}
                <input type="file" accept="image/*" onChange={(ev) => onFileChange("cachet", ev.target.files?.[0])} disabled={saving} />
              </div>
            </div>
            <div className="stp-field">
              <span className="stp-field-label">Logo du pays</span>
              <div className="stp-etab-file-row">
                {logoPaysPreview && <img className="stp-etab-img-sm" src={logoPaysPreview} alt="Logo pays preview" />}
                <input type="file" accept="image/*" onChange={(ev) => onFileChange("logo_pays", ev.target.files?.[0])} disabled={saving} />
              </div>
            </div>

            <div className="stp-etab-info-box">
              <span><strong>Type :</strong> {typeLabel}</span>
              <span><strong>Statut :</strong> {detail.active ? "Actif" : "Inactif"}</span>
              <span><strong>Cycles :</strong> {cyclesLabel}</span>
              <span style={{ flexBasis: "100%" }}><i className="ti ti-info-circle" /> Ces informations sont gérées par le super administrateur.</span>
            </div>

            <div className="stp-add-form-actions">
              <button type="button" className="stp-cta stp-cta-outline stp-cta-sm" onClick={onCancelEdit} disabled={saving}>Annuler</button>
              <button type="submit" className="stp-cta stp-cta-sm" disabled={saving}>{saving ? <span className="stp-spinner" /> : "Enregistrer"}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// Panneau : configuration carte scolaire (liste + activation + CRUD)
// ════════════════════════════════════════════════════════════
function CartePanel({
  loading, list, annees,
  addOpen, editingId, form, onFormChange,
  onOpenAdd, onCancelAdd, onStartEdit, onCancelEdit,
  saving, onSubmit,
  deletingId, deleteBusyId, onAskDelete, onCancelDelete, onConfirmDelete,
  activatingId, onActivate,
}) {
  const formOpen = addOpen || !!editingId;

  return (
    <div className="stp-panel">
      <div className="stp-panel-head">
        <span className="stp-panel-title">Configurations carte scolaire</span>
        <span className="stp-panel-count">{loading ? "…" : `${list.length} configuration${list.length > 1 ? "s" : ""}`}</span>
      </div>

      {loading ? (
        <div className="stp-panel-loading"><span className="stp-spinner-dark" /> Chargement…</div>
      ) : (
        <ul className="stp-list">
          {list.length === 0 && <li className="stp-panel-empty">Aucune configuration pour le moment.</li>}
          {list.map(item => {
            const isDeleting = deletingId === item.id;
            const isDeleteBusy = deleteBusyId === item.id;

            if (isDeleting) {
              return (
                <li key={item.id} className="stp-list-row stp-list-row-deleting">
                  <div className="stp-delete-confirm">
                    <span className="stp-delete-confirm-text">Supprimer la configuration « {item.annee_scolaire_nom} » ?</span>
                    <div className="stp-delete-confirm-actions">
                      <button type="button" className="stp-cta stp-cta-outline stp-cta-sm" onClick={onCancelDelete} disabled={isDeleteBusy}>Annuler</button>
                      <button type="button" className="stp-cta stp-cta-danger stp-cta-sm" onClick={() => onConfirmDelete(item.id)} disabled={isDeleteBusy}>
                        {isDeleteBusy ? <span className="stp-spinner" /> : "Supprimer"}
                      </button>
                    </div>
                  </div>
                </li>
              );
            }

            return (
              <li key={item.id} className="stp-list-row">
                <span className="stp-row-name">{item.annee_scolaire_nom}</span>
                {item.coordonnees_carte && (
                  <span className="stp-row-tag" style={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {item.coordonnees_carte}
                  </span>
                )}
                {item.actif ? (
                  <span className="stp-row-tag stp-row-tag-active">Active</span>
                ) : (
                  <button type="button" className="stp-cta stp-cta-ghost stp-cta-sm" onClick={() => onActivate(item.id)} disabled={activatingId === item.id}>
                    {activatingId === item.id ? <span className="stp-spinner" /> : "Activer"}
                  </button>
                )}
                <div className="stp-row-actions">
                  <button type="button" className="stp-row-btn" onClick={() => onStartEdit(item)} title="Modifier"><i className="ti ti-pencil" /></button>
                  <button type="button" className="stp-row-btn stp-row-btn-danger" onClick={() => onAskDelete(item)} title="Supprimer"><i className="ti ti-trash" /></button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div className="stp-panel-footer">
        {!formOpen ? (
          <button type="button" className="stp-add-trigger" onClick={onOpenAdd}>
            <i className="ti ti-plus" />Ajouter une configuration
          </button>
        ) : (
          <form className="stp-add-form" onSubmit={(ev) => { ev.preventDefault(); onSubmit(); }}>
            <span className="stp-add-form-label">{editingId ? "Modifier la configuration" : "Nouvelle configuration"}</span>
            <select className="stp-add-select" style={{ flex: 1 }} value={form.annee_scolaire} onChange={(ev) => onFormChange("annee_scolaire", ev.target.value)} disabled={saving}>
              <option value="">Année scolaire...</option>
              {annees.map(a => <option key={a.id} value={a.id}>{a.nom}</option>)}
            </select>
            <input className="stp-add-input" placeholder="Ex: BOBO-DIOULASSO • Tél : 88 99 88 99"
              value={form.coordonnees_carte} maxLength={150}
              onChange={(ev) => onFormChange("coordonnees_carte", ev.target.value)} disabled={saving} />
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "#334155" }}>
              <input type="checkbox" checked={form.actif} onChange={(ev) => onFormChange("actif", ev.target.checked)} disabled={saving} />
              Configuration active
            </label>
            <div style={{ fontSize: 11.5, color: "#94A3B8" }}>
              <i className="ti ti-info-circle" /> Le nom, le logo, le cachet et le pays viennent des informations de l'établissement.
            </div>
            <div className="stp-add-form-actions">
              <button type="button" className="stp-cta stp-cta-outline stp-cta-sm" onClick={editingId ? onCancelEdit : onCancelAdd} disabled={saving}>Annuler</button>
              <button type="submit" className="stp-cta stp-cta-sm" disabled={saving}>{saving ? <span className="stp-spinner" /> : (editingId ? "Enregistrer" : "Ajouter")}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// Éditeur d'appréciations (seuil ≥ note → texte), utilisé par le panneau bulletin
// ════════════════════════════════════════════════════════════
function StpAppreciationEditor({ label, rows, setRows, disabled }) {
  const addRow = () => setRows(prev => [...prev, { seuil: "", texte: "" }]);
  const removeRow = (i) => setRows(prev => prev.filter((_, idx) => idx !== i));
  const updateRow = (i, field, value) => setRows(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: value } : r));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span className="stp-add-form-label">{label}</span>
      {rows.map((r, i) => (
        <div key={i} className="stp-appr-row">
          <input type="number" step="0.5" placeholder="Seuil" className="stp-appr-seuil" value={r.seuil}
            onChange={(ev) => updateRow(i, "seuil", ev.target.value)} disabled={disabled} />
          <input type="text" placeholder="Appréciation" className="stp-appr-texte" value={r.texte}
            onChange={(ev) => updateRow(i, "texte", ev.target.value)} disabled={disabled} />
          <button type="button" className="stp-row-btn stp-row-btn-danger" onClick={() => removeRow(i)} disabled={disabled} title="Retirer">
            <i className="ti ti-trash" />
          </button>
        </div>
      ))}
      <button type="button" className="stp-add-trigger" style={{ padding: "4px 0" }} onClick={addRow} disabled={disabled}>
        <i className="ti ti-plus" />Ajouter un seuil
      </button>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// Panneau : configuration bulletin (liste + activation + CRUD + appréciations)
// ════════════════════════════════════════════════════════════
function BulletinPanel({
  loading, list, annees, cycles,
  addOpen, editingId, form, onFormChange,
  rowsEducateur, setRowsEducateur, rowsEcole, setRowsEcole,
  onOpenAdd, onCancelAdd, onStartEdit, onCancelEdit,
  saving, onSubmit,
  deletingId, deleteBusyId, onAskDelete, onCancelDelete, onConfirmDelete,
  activatingId, onActivate,
}) {
  const formOpen = addOpen || !!editingId;
  const cycleLabel = (val) => CYCLE_LABELS[val] || val;

  return (
    <div className="stp-panel">
      <div className="stp-panel-head">
        <span className="stp-panel-title">Configurations bulletin</span>
        <span className="stp-panel-count">{loading ? "…" : `${list.length} configuration${list.length > 1 ? "s" : ""}`}</span>
      </div>

      {loading ? (
        <div className="stp-panel-loading"><span className="stp-spinner-dark" /> Chargement…</div>
      ) : (
        <ul className="stp-list">
          {list.length === 0 && <li className="stp-panel-empty">Aucune configuration pour le moment.</li>}
          {list.map(item => {
            const isDeleting = deletingId === item.id;
            const isDeleteBusy = deleteBusyId === item.id;

            if (isDeleting) {
              return (
                <li key={item.id} className="stp-list-row stp-list-row-deleting">
                  <div className="stp-delete-confirm">
                    <span className="stp-delete-confirm-text">Supprimer « {cycleLabel(item.cycle)} — {item.annee_scolaire_nom} » ?</span>
                    <div className="stp-delete-confirm-actions">
                      <button type="button" className="stp-cta stp-cta-outline stp-cta-sm" onClick={onCancelDelete} disabled={isDeleteBusy}>Annuler</button>
                      <button type="button" className="stp-cta stp-cta-danger stp-cta-sm" onClick={() => onConfirmDelete(item.id)} disabled={isDeleteBusy}>
                        {isDeleteBusy ? <span className="stp-spinner" /> : "Supprimer"}
                      </button>
                    </div>
                  </div>
                </li>
              );
            }

            return (
              <li key={item.id} className="stp-list-row">
                <span className="stp-row-name">{cycleLabel(item.cycle)} — {item.annee_scolaire_nom}</span>
                <span className="stp-row-tag">note/{item.note_maximale}</span>
                {item.est_actif ? (
                  <span className="stp-row-tag stp-row-tag-active">Active</span>
                ) : (
                  <button type="button" className="stp-cta stp-cta-ghost stp-cta-sm" onClick={() => onActivate(item.id)} disabled={activatingId === item.id}>
                    {activatingId === item.id ? <span className="stp-spinner" /> : "Activer"}
                  </button>
                )}
                <div className="stp-row-actions">
                  <button type="button" className="stp-row-btn" onClick={() => onStartEdit(item)} title="Modifier"><i className="ti ti-pencil" /></button>
                  <button type="button" className="stp-row-btn stp-row-btn-danger" onClick={() => onAskDelete(item)} title="Supprimer"><i className="ti ti-trash" /></button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div className="stp-panel-footer">
        {!formOpen ? (
          <button type="button" className="stp-add-trigger" onClick={onOpenAdd} disabled={cycles.length === 0}>
            <i className="ti ti-plus" />
            {cycles.length === 0 ? "Aucun cycle configuré pour l'établissement" : "Ajouter une configuration"}
          </button>
        ) : (
          <form className="stp-add-form" onSubmit={(ev) => { ev.preventDefault(); onSubmit(); }}>
            <span className="stp-add-form-label">{editingId ? "Modifier la configuration" : "Nouvelle configuration"}</span>
            <div className="stp-add-form-row">
              <select className="stp-add-select" value={form.cycle} onChange={(ev) => onFormChange("cycle", ev.target.value)} disabled={saving}>
                <option value="">Cycle...</option>
                {cycles.map(c => <option key={c} value={c}>{cycleLabel(c)}</option>)}
              </select>
              <select className="stp-add-select" value={form.annee_scolaire} onChange={(ev) => onFormChange("annee_scolaire", ev.target.value)} disabled={saving}>
                <option value="">Année...</option>
                {annees.map(a => <option key={a.id} value={a.id}>{a.nom}</option>)}
              </select>
              <input type="number" step="0.01" className="stp-add-coef" placeholder="Note max" value={form.note_maximale}
                onChange={(ev) => onFormChange("note_maximale", ev.target.value)} disabled={saving} />
            </div>

            <div style={{ borderTop: "1px solid rgba(15,23,42,0.08)", paddingTop: 8, marginTop: 4 }}>
              <StpAppreciationEditor label="Appréciation éducateur (seuil ≥ note → texte)" rows={rowsEducateur} setRows={setRowsEducateur} disabled={saving} />
            </div>
            <div style={{ borderTop: "1px solid rgba(15,23,42,0.08)", paddingTop: 8, marginTop: 4 }}>
              <StpAppreciationEditor label="Appréciation école (seuil ≥ note → texte)" rows={rowsEcole} setRows={setRowsEcole} disabled={saving} />
            </div>

            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "#334155", marginTop: 4 }}>
              <input type="checkbox" checked={form.est_actif} onChange={(ev) => onFormChange("est_actif", ev.target.checked)} disabled={saving} />
              Configuration active
            </label>

            <div style={{ fontSize: 11.5, color: "#94A3B8" }}>
              <i className="ti ti-info-circle" /> Le slogan, l'adresse et les logos viennent des informations de l'établissement.
            </div>

            <div className="stp-add-form-actions">
              <button type="button" className="stp-cta stp-cta-outline stp-cta-sm" onClick={editingId ? onCancelEdit : onCancelAdd} disabled={saving}>Annuler</button>
              <button type="submit" className="stp-cta stp-cta-sm" disabled={saving}>{saving ? <span className="stp-spinner" /> : (editingId ? "Enregistrer" : "Ajouter")}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}